/**
 * The maintenance announcement (/maintenance.json, written by deploy/server/maintenance.sh and
 * served by Caddy): nothing, a maintenance announced for a time, or the maintenance itself.
 */
export type MaintenanceStatus = { phase: 'none' } | { phase: 'announced'; startsAt: string } | { phase: 'maintenance' };

export interface MaintenanceView {
  phase: 'none' | 'announced' | 'maintenance';
  /** Whole seconds until an announced maintenance starts. */
  secondsLeft: number | null;
}

/** Reads the file's JSON; anything unexpected (the plain local stack, an error page) is "none". */
export function parseStatus(value: unknown): MaintenanceStatus {
  if (value && typeof value === 'object' && 'phase' in value) {
    const v = value as { phase?: unknown; startsAt?: unknown };
    if (v.phase === 'maintenance') return { phase: 'maintenance' };
    if (v.phase === 'announced' && typeof v.startsAt === 'string' && !Number.isNaN(Date.parse(v.startsAt))) {
      return { phase: 'announced', startsAt: v.startsAt };
    }
  }
  return { phase: 'none' };
}

/**
 * What to show now. `skewMs` is the server's clock minus this computer's (from the response's
 * Date header), so a computer whose clock is off still counts down to the right moment. An
 * announced maintenance whose time has come counts as begun.
 */
export function maintenanceView(status: MaintenanceStatus, nowMs: number, skewMs = 0): MaintenanceView {
  if (status.phase === 'none') return { phase: 'none', secondsLeft: null };
  if (status.phase === 'maintenance') return { phase: 'maintenance', secondsLeft: null };
  const left = Math.ceil((Date.parse(status.startsAt) - (nowMs + skewMs)) / 1000);
  return left > 0 ? { phase: 'announced', secondsLeft: left } : { phase: 'maintenance', secondsLeft: null };
}

/** 105 → "1:45". */
export function formatCountdown(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** How often to read the announcement: often while something is coming, rarely otherwise. */
export function pollIntervalMs(view: MaintenanceView): number {
  return view.phase === 'none' ? 30_000 : 5_000;
}
